import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Calendar, TrendingUp, BarChart3, CheckCircle2 } from 'lucide-react';

export default function HiringManagerDashboard() {
  const stats = [
    { label: 'Open Positions', value: '5', change: '+1', trend: 'up' },
    { label: 'Candidates in Pipeline', value: '42', change: '+8', trend: 'up' },
    { label: 'Pending Interviews', value: '12', change: '-3', trend: 'down' },
    { label: 'Offers Extended', value: '3', change: '+2', trend: 'up' },
  ];

  const openPositions = [
    {
      id: '1',
      title: 'Senior Software Engineer',
      department: 'Engineering',
      daysOpen: 15,
      candidates: 45,
      inReview: 12,
      interviews: 5,
      offers: 1,
    },
    {
      id: '2',
      title: 'Product Manager',
      department: 'Product',
      daysOpen: 22,
      candidates: 32,
      inReview: 8,
      interviews: 3,
      offers: 0,
    },
    {
      id: '3',
      title: 'UX Designer',
      department: 'Design',
      daysOpen: 8,
      candidates: 28,
      inReview: 10,
      interviews: 2,
      offers: 0,
    },
  ];

  const upcomingInterviews = [
    { candidate: 'Sarah Johnson', position: 'Senior Software Engineer', date: 'Today, 2:00 PM', type: 'Technical' },
    { candidate: 'Michael Chen', position: 'Product Manager', date: 'Today, 4:00 PM', type: 'Behavioral' },
    { candidate: 'Emily Davis', position: 'UX Designer', date: 'Tomorrow, 10:00 AM', type: 'Portfolio Review' },
  ];

  const hiringMetrics = [
    { metric: 'Average Time to Hire', value: '28 days', target: '30 days', status: 'on-track' },
    { metric: 'Offer Accept Rate', value: '85%', target: '80%', status: 'above' },
    { metric: 'Quality of Hire', value: '4.2/5', target: '4.0/5', status: 'above' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Hiring Manager Dashboard</h1>
          <p className="text-gray-600 mt-2">Oversee hiring pipeline and make final decisions</p>
        </div>

        {/* Stats */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">{stat.label}</p>
                {stat.trend === 'up' ? (
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    {stat.change}
                  </span>
                ) : (
                  <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded-full">
                    {stat.change}
                  </span>
                )}
              </div>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Open Positions */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Open Positions</CardTitle>
                  <Button size="sm" variant="outline">Create Position</Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {openPositions.map((position) => (
                    <div key={position.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-gray-900">{position.title}</h4>
                          <p className="text-sm text-gray-600">{position.department} • Open for {position.daysOpen} days</p>
                        </div>
                        <Badge variant={position.daysOpen > 30 ? 'warning' : 'info'}>
                          {position.daysOpen > 30 ? 'Urgent' : 'Active'}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Candidates</p>
                          <p className="text-lg font-semibold text-gray-900">{position.candidates}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">In Review</p>
                          <p className="text-lg font-semibold text-primary-600">{position.inReview}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Interviews</p>
                          <p className="text-lg font-semibold text-secondary-600">{position.interviews}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Offers</p>
                          <p className="text-lg font-semibold text-green-600">{position.offers}</p>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline">View Pipeline</Button>
                        <Button size="sm">Review Candidates</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Interviews */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Upcoming Interviews
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingInterviews.map((interview, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3">
                      <p className="font-semibold text-gray-900 text-sm mb-1">{interview.candidate}</p>
                      <p className="text-xs text-gray-600 mb-2">{interview.position}</p>
                      <div className="flex items-center justify-between">
                        <div className="text-xs">
                          <p className="text-gray-500">{interview.date}</p>
                          <Badge size="sm" variant="info" className="mt-1">{interview.type}</Badge>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="w-full mt-3">Join / View</Button>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" className="w-full mt-4">
                  <Calendar className="h-4 w-4 mr-2" />
                  View Full Calendar
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Hiring Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-primary-600" />
              Hiring Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-3">
              {hiringMetrics.map((metric) => (
                <div key={metric.metric} className="border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">{metric.metric}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                      <p className="text-xs text-gray-500 mt-1">Target: {metric.target}</p>
                    </div>
                    {metric.status === 'above' ? (
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    ) : (
                      <CheckCircle2 className="h-6 w-6 text-blue-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg">
              <h6 className="font-semibold text-gray-900 mb-2">📊 Performance Summary</h6>
              <p className="text-sm text-gray-700">
                Your team is performing above target on 2 of 3 key metrics. Average time to hire is 2 days ahead of schedule.
                Keep up the excellent work!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Decision Queue */}
        <Card>
          <CardHeader>
            <CardTitle>Decisions Awaiting Your Approval</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { candidate: 'Sarah Johnson', action: 'Final Interview Approval', position: 'Senior Software Engineer' },
                { candidate: 'Michael Chen', action: 'Offer Package Review', position: 'Product Manager' },
                { candidate: 'Emily Davis', action: 'Shortlist Approval', position: 'UX Designer' },
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-semibold text-gray-900">{item.candidate}</p>
                    <p className="text-sm text-gray-600">{item.action} • {item.position}</p>
                  </div>
                  <div className="flex space-x-2">
                    <Button size="sm" variant="outline">Review</Button>
                    <Button size="sm">Approve</Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
